import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  SubmitButton,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { parseFormSchema } from "@/lib/formSchema";
import {
  PortalFormRenderer,
  findFirstMissingRequired,
  findFirstFormatError,
  type PortalFormData,
} from "../components/PortalFormRenderer";
import { portalPackagesApi } from "../api/packages";
import type { EducationContentDownloadDto, PortalItemDto } from "../api/types";
import { InHouseSigningSurface } from "./InHouseSigningSurface";
import { SubmissionStatusCard } from "../components/SubmissionStatusCard";
import { ResponseBlockedHint, type PortalResponse } from "../components/ResponseStep";
import { SIGNATURE_STEP_NOT_SET_UP, signingRouteFor } from "../lib/signingRoute";

// Unified External-Party Onboarding & Collection Platform — Series 3.
// Education fulfilment surface. Presents the content (document and/or
// video), enforces an optional MinimumDurationSeconds gate before submit,
// shows the optional questions schema, captures the recipient's
// attestation (full name + checkbox), and POSTs to submit-education.
//
// When the template requires a signature, we delegate the entire
// acknowledgment step to InHouseSigningSurface — that flow renders the
// content/answers block + signature into the final PDF and stages it,
// avoiding duplicate StagedSubmissions for the same item.

interface EducationItemSurfaceProps {
  item: PortalItemDto;
  onUpdated: (next: PortalItemDto) => void;
  disabled?: boolean;
  /** Review loop — set while the item owes a response; the note is sent with
   *  the final action (acknowledgment submit, or the in-house signature). */
  response?: PortalResponse | null;
  /** Reports the in-house signature POST being in flight. */
  onSigningBusyChange?: (busy: boolean) => void;
}

export function EducationItemSurface({
  item,
  onUpdated,
  disabled,
  response,
  onSigningBusyChange,
}: EducationItemSurfaceProps) {
  const toast = useToast();
  // The server-enforced signing route decides; a DocuSign route can't be
  // fulfilled from an Education item and is shown blocked (A18).
  const signingRoute = signingRouteFor(item);
  const requiresInHouseSignature = signingRoute === "InHouse";

  const schema = useMemo(() => parseFormSchema(item.formSchemaJson), [item.formSchemaJson]);
  const hasQuestions = !!schema && schema.fields.length > 0;
  const minDuration = item.educationMinimumDurationSeconds ?? 0;

  const [content, setContent] = useState<EducationContentDownloadDto | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);

  const [answers, setAnswers] = useState<PortalFormData>(() => {
    if (!item.savedFormDataJson) return {};
    try {
      const parsed = JSON.parse(item.savedFormDataJson) as unknown;
      return parsed && typeof parsed === "object" ? (parsed as PortalFormData) : {};
    } catch {
      return {};
    }
  });
  const [attestName, setAttestName] = useState("");
  const [attestChecked, setAttestChecked] = useState(false);
  const [secondsViewed, setSecondsViewed] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const dto = await portalPackagesApi.getEducationContent(item.id);
        if (!cancelled) setContent(dto);
      } catch (err) {
        if (!cancelled) {
          setContentError(err instanceof Error ? err.message : "Couldn't load this content.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.id]);

  // Honor-system view-time counter — ticks one second at a time, used to
  // gate the submit button when MinimumDurationSeconds is set. Stops
  // counting once the threshold is reached. Pauses when the tab is
  // hidden so a backgrounded tab cannot satisfy the gate by accident.
  const visibleRef = useRef(true);
  useEffect(() => {
    if (minDuration <= 0) return;
    const onVisibility = () => {
      visibleRef.current = document.visibilityState === "visible";
    };
    document.addEventListener("visibilitychange", onVisibility);
    const id = window.setInterval(() => {
      if (!visibleRef.current) return;
      setSecondsViewed((s) => (s >= minDuration ? s : s + 1));
    }, 1000);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [minDuration]);

  const minMet = minDuration <= 0 || secondsViewed >= minDuration;

  const submitAcknowledgment = useSubmittingMutation(
    async () => {
      const next = await portalPackagesApi.submitEducation(item.id, {
        answersJson: hasQuestions ? JSON.stringify(answers) : null,
        attestationFullName: attestName.trim(),
        acknowledged: true,
        ...(response ? { responseNote: response.note } : {}),
      });
      onUpdated(next);
      toast.success("Submitted ✓ — thanks, your acknowledgment is recorded.");
    },
    {
      onError: (err) =>
        setSubmitError(err instanceof Error ? err.message : "Could not submit. Try again."),
    },
  );
  const submitting = submitAcknowledgment.pending;

  const handleSubmit = useCallback(() => {
    setSubmitError(null);
    if (response?.blockedReason) return;
    if (!attestChecked) {
      setSubmitError("Please confirm you've reviewed the content.");
      return;
    }
    if (!attestName.trim()) {
      setSubmitError("Please enter your full name to acknowledge.");
      return;
    }
    if (hasQuestions && schema) {
      const missing = findFirstMissingRequired(schema, answers);
      if (missing) {
        setSubmitError(`Please answer "${missing.label}" before submitting.`);
        return;
      }
      const formatError = findFirstFormatError(schema, answers);
      if (formatError) {
        setSubmitError(`"${formatError.field.label}" — ${formatError.message}`);
        return;
      }
    }
    void submitAcknowledgment.submit();
  }, [attestChecked, attestName, hasQuestions, schema, answers, submitAcknowledgment, response]);

  // For signature-required Education items, the in-house signing surface
  // owns the final PDF (content block + answers + signature). We persist
  // the answers via save-form-data first so the signing service can read
  // them out of SavedFormDataJson. Declared before any early return so the
  // hook call order stays stable (react-hooks/rules-of-hooks).
  const [signingReady, setSigningReady] = useState(false);
  const [signingPrepError, setSigningPrepError] = useState<string | null>(null);
  const prepareForSigning = useCallback(async () => {
    setSigningPrepError(null);
    try {
      if (hasQuestions) {
        await portalPackagesApi.saveFormData(item.id, JSON.stringify(answers));
      }
      setSigningReady(true);
    } catch (err) {
      setSigningPrepError(
        err instanceof Error ? err.message : "Couldn't save your answers before signing.",
      );
    }
  }, [hasQuestions, answers, item.id]);

  if (disabled && item.status === "SubmittedForReview") {
    return (
      <SubmissionStatusCard tone="review" title="Submitted for review" data-testid="education-under-review">
        Your acknowledgment is under review.
      </SubmissionStatusCard>
    );
  }

  if (signingRoute === "DocuSign" && !disabled) {
    return (
      <p className="text-small text-status-error m-0" role="alert" data-testid="signature-step-blocked">
        {SIGNATURE_STEP_NOT_SET_UP}
      </p>
    );
  }

  if (requiresInHouseSignature) {
    return (
      <div className="flex flex-col gap-space-4">
        <ContentViewer
          content={content}
          error={contentError}
          minDuration={minDuration}
          secondsViewed={secondsViewed}
        />
        {hasQuestions && schema && (
          <PortalFormRenderer
            schema={schema}
            value={answers}
            onChange={setAnswers}
            disabled={disabled || !minMet || signingReady}
          />
        )}
        {!minMet && (
          <p className="text-small text-status-warning m-0">
            Please continue reviewing for at least {minDuration - secondsViewed} more second
            {minDuration - secondsViewed === 1 ? "" : "s"} before signing.
          </p>
        )}
        {signingPrepError && (
          <p className="text-small text-status-error m-0" role="alert">
            {signingPrepError}
          </p>
        )}
        {minMet && !signingReady && (
          <div className="flex items-center justify-end">
            <Button onClick={() => void prepareForSigning()} data-testid="education-continue-to-sign">
              Continue to sign
            </Button>
          </div>
        )}
        {minMet && signingReady && (
          <InHouseSigningSurface
            item={item}
            onUpdated={onUpdated}
            response={response}
            onBusyChange={onSigningBusyChange}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-4">
      {!disabled && item.status === "InProgress" && !submitting && (
        <SubmissionStatusCard tone="progress" title="In progress" data-testid="education-in-progress">
          You've started this — review the content and submit your acknowledgment when you're ready.
        </SubmissionStatusCard>
      )}
      <ContentViewer
        content={content}
        error={contentError}
        minDuration={minDuration}
        secondsViewed={secondsViewed}
      />

      {hasQuestions && schema && (
        <PortalFormRenderer
          schema={schema}
          value={answers}
          onChange={setAnswers}
          disabled={disabled || submitting || !minMet}
        />
      )}

      {!disabled && (
        <div className="p-space-3 border border-border-light rounded-md bg-white-pure flex flex-col gap-space-2">
          <label className="flex items-start gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              checked={attestChecked}
              onChange={(e) => setAttestChecked(e.target.checked)}
              disabled={!minMet || submitting}
              data-testid="education-attest-check"
            />
            <span>
              I have reviewed the content above and understand it.
            </span>
          </label>
          <label className="flex flex-col gap-space-1 text-small text-onyx">
            <span>Type your full name to acknowledge</span>
            <input
              type="text"
              value={attestName}
              onChange={(e) => setAttestName(e.target.value)}
              disabled={!minMet || submitting}
              className="border border-border-light rounded-md p-space-2 text-onyx"
              data-testid="education-attest-name"
            />
          </label>
        </div>
      )}

      {submitError && (
        <p className="text-small text-status-error m-0" role="alert">
          {submitError}
        </p>
      )}

      {!disabled && (
        <div className="flex flex-col items-end gap-space-2">
          <SubmitButton
            onClick={handleSubmit}
            pending={submitting}
            pendingLabel="Submitting…"
            disabled={!minMet || !!response?.blockedReason}
            data-testid="education-submit"
          >
            Submit acknowledgment
          </SubmitButton>
          <ResponseBlockedHint response={response} />
        </div>
      )}
      {submitting && (
        <SubmissionStatusCard tone="progress" title="Submitting…" data-testid="education-submitting">
          We're recording your acknowledgment. This can take a moment — please don't close this tab.
        </SubmissionStatusCard>
      )}
    </div>
  );
}

interface ContentViewerProps {
  content: EducationContentDownloadDto | null;
  error: string | null;
  minDuration: number;
  secondsViewed: number;
}

function ContentViewer({ content, error, minDuration, secondsViewed }: ContentViewerProps) {
  if (error) {
    return <p className="text-small text-status-error" role="alert">{error}</p>;
  }
  if (!content) {
    return <p className="text-small text-gray-mid">Loading content…</p>;
  }
  const isVideoBlob = content.contentType?.startsWith("video/") ?? false;
  return (
    <div className="flex flex-col gap-space-3">
      {content.contentUrl && !isVideoBlob && (
        <div className="border border-border-light rounded-md overflow-hidden bg-white-warm">
          <iframe
            src={content.contentUrl}
            title={content.originalFileName ?? "Education content"}
            className="w-full"
            style={{ height: "60dvh", minHeight: "400px" }}
          />
          <p className="text-caption text-gray-mid m-0 p-space-2">
            {content.originalFileName ?? "Content"}
            {" · "}
            <a href={content.contentUrl} target="_blank" rel="noopener noreferrer" className="underline">
              Open in new tab
            </a>
          </p>
        </div>
      )}
      {content.contentUrl && isVideoBlob && (
        <video
          src={content.contentUrl}
          controls
          className="w-full rounded-md border border-border-light"
        />
      )}
      {content.videoUrl && (
        <div className="border border-border-light rounded-md overflow-hidden bg-onyx">
          <iframe
            src={content.videoUrl}
            title="Education video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full"
            style={{ aspectRatio: "16/9", border: 0 }}
          />
        </div>
      )}
      {minDuration > 0 && (
        <p
          className="text-caption text-gray-mid m-0"
          data-testid="education-view-progress"
        >
          Viewing time: {Math.min(secondsViewed, minDuration)} / {minDuration} seconds
        </p>
      )}
    </div>
  );
}
